import { queryNamespace } from '@libs/shared/frontend/helpers/query-namespace.helper';
import { queryOptions } from '@tanstack/react-query';

import { client, HttpException, type CustomerAuditLogEntityTypeEnum, type operations } from '@/libs/openapi';
import { fromApiCustomerAuditLogFieldAdapter } from '@/resources/audit-log/audit-log.field-adapter';
import type { QueryResultData } from '@/types/query-result-data.type';
import { Resource } from '@/types/resource.type';

type AuditLogListParams =
  | operations['CustomerAuditLogController_getMany']['parameters']['query']
  | (operations['CustomerAuditLogController_getCampaignRequestAuditLogs']['parameters']['query'] & {
    id: number;
    resource: Resource.campaignRequest;
  })
  | (operations['CustomerAuditLogController_getCampaignRequestProgramAuditLogs']['parameters']['query'] & {
    id: number;
    resource: Resource.campaignRequestProgram;
  });

export const auditLogQueries = queryNamespace(Resource.activityLog, {
  list: (params?: AuditLogListParams) => queryOptions({
    queryKey: [ params ],
    queryFn: async ({ signal }) => {
      if (params && 'resource' in params) {
        const query = {
          limit: params.limit ?? 50,
          page: params.page ?? 1,
          createdAtGte: params.createdAtGte,
          createdAtLte: params.createdAtLte,
        };

        const { data, error, response } = await (
          params.resource === Resource.campaignRequest
            ? client.GET('/customer/api/tcd/campaign-requests/{id}/audit-logs', {
              signal,
              params: { path: { id: params.id }, query },
            })
            : client.GET('/customer/api/tcd/programs/{id}/audit-logs', {
              signal,
              params: { path: { id: params.id }, query },
            })
        );

        if (error) {
          throw new HttpException({
            message: Array.isArray(error.message) ? error.message[0] : error.message,
            response,
            isGlobal: false,
          });
        }

        return { data: data.items, totalCount: data.totalCount };
      }

      const { data, error, response } = await client.GET('/customer/api/tcd/audit-logs', {
        signal,
        params: {
          query: {
            limit: params?.limit ?? 30,
            page: params?.page ?? 1,
            entityTypes: params?.entityTypes as unknown as CustomerAuditLogEntityTypeEnum,
            organizationUserId: params?.organizationUserId,
            createdAtGte: params?.createdAtGte,
            createdAtLte: params?.createdAtLte,
          },
        },
      });

      if (error) {
        throw new HttpException({
          message: Array.isArray(error.message) ? error.message[0] : error.message,
          response,
          isGlobal: false,
        });
      }

      return { data: data.items, totalCount: data.totalCount };
    },
    select: (data) => ({
      totalCount: data.totalCount,
      data: data.data.map((row) => ({
        original: row,
        transformed: {
          forLabels: fromApiCustomerAuditLogFieldAdapter.transform(row),
        },
        meta: {
          actorDisplayName: fromApiCustomerAuditLogFieldAdapter.getActorDisplayName(row),
        },
      })),
    }),
  }),
});

export type AuditLogTableRow = QueryResultData<typeof auditLogQueries.list>['data'][number];
